import { logout, refreshToken } from "@/redux/slices/login.slice";
import store from "@/redux/store";
import axios from "axios";

// Create an Axios instance
const ehjzleeInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

type FailedRequest = {
  resolve: (token: string | null) => void;
  reject: (error: any) => void;
};

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;
let failedRequestsQueue: FailedRequest[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedRequestsQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedRequestsQueue = [];
};

// Request interceptor
ehjzleeInstance.interceptors.request.use(
  (config) => {
    // Add auth token or other headers
    const token = store.getState().login.accessToken; // Or use a context/store
    if (token && config.headers["without-token"] !== "true") {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },

  (error) => {
    // Do something with request error
    return Promise.reject(error);
  }
);

// Response interceptor
// Response interceptor
ehjzleeInstance.interceptors.response.use(
  (response) => {
    // Any status code that lies within the range of 2xx causes this function to trigger
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // If a token refresh is in progress, add this request to the queue
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return ehjzleeInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      // Start token refresh process
      isRefreshing = true;
      originalRequest._retry = true;

      try {
        const newAccessToken = await refreshAccessToken();

        // If token refresh succeeds, process all queued requests
        if (newAccessToken) {
          processQueue(null, newAccessToken);
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
          return ehjzleeInstance(originalRequest);
        }

        const refreshError = new Error("Unable to refresh access token");
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      } catch (refreshError) {
        // If token refresh fails, reject all queued requests
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);


export default ehjzleeInstance;

const refreshAccessToken = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = refreshAccessTokenRequest().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

const refreshAccessTokenRequest = async () => {
  try {
    const currentRefreshToken = store.getState().login.refreshToken;
    if (!currentRefreshToken) {
      store.dispatch(logout());
      return null;
    }

    const { data } = await axios.post(
      `${process.env.NEXT_PUBLIC_API_BASE_URL}/auth/token/refresh`,
      {
        refreshToken: currentRefreshToken,
      }
    );
    store.dispatch(
      refreshToken({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        firebaseToken: data.firebaseToken,
      })
    );

    return data.accessToken;
  } catch (error) {
    console.error("Failed to refresh access token:", error);
    store.dispatch(logout());
    return null;
  }
};

const getJwtExpiry = (token: string | null) => {
  if (!token) return null;

  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const normalizedPayload = payload
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(payload.length / 4) * 4, "=");
    const decodedPayload = JSON.parse(atob(normalizedPayload));
    return typeof decodedPayload.exp === "number" ? decodedPayload.exp : null;
  } catch {
    return null;
  }
};

const isAccessTokenFresh = (token: string | null, bufferSeconds = 30) => {
  const expiry = getJwtExpiry(token);
  if (!expiry) return false;
  return expiry - bufferSeconds > Math.floor(Date.now() / 1000);
};

export const ensureValidAccessToken = async () => {
  const { accessToken, refreshToken: currentRefreshToken } =
    store.getState().login;

  if (isAccessTokenFresh(accessToken)) {
    return accessToken;
  }

  if (!currentRefreshToken) {
    store.dispatch(logout());
    return null;
  }

  return refreshAccessToken();
};
